/**
 * CalendarGrid – month navigation + 42-day month grid (Monday-first).
 * Presentational; drives DatePicker (and reusable for other date popovers).
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo } from 'react';
import {
  addDays,
  addMonths,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';

import { WEEKDAYS } from './date-time-picker-utils';

export interface CalendarGridProps {
  viewMonth: Date;
  onViewMonthChange: (updater: (m: Date) => Date) => void;
  selectedDate: Date | null;
  onDaySelect: (d: Date) => void;
  isDayDisabled?: (d: Date) => boolean;
  compact?: boolean;
}

/** 42 days (6 weeks) covering `viewMonth`, starting on Monday. */
function buildMonthGrid(viewMonth: Date): Date[] {
  const start = startOfMonth(viewMonth);
  const startPad = start.getDay() === 0 ? 6 : start.getDay() - 1;
  const from = addDays(start, -startPad);
  return Array.from({ length: 42 }, (_, i) => addDays(from, i));
}

export const CalendarGrid: FC<CalendarGridProps> = ({
  viewMonth,
  onViewMonthChange,
  selectedDate,
  onDaySelect,
  isDayDisabled,
  compact = false,
}) => {
  const days = useMemo(() => buildMonthGrid(viewMonth), [viewMonth]);
  const navClass = compact ? 'h-7 w-7' : 'h-8 w-8';
  const dayClass = compact ? 'h-8 w-8 text-xs' : 'h-9 w-9 text-sm';

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between px-0.5">
        <button
          type="button"
          onClick={() => onViewMonthChange((m) => subMonths(m, 1))}
          className={cn(
            'flex items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            navClass,
          )}
          aria-label="Previous month"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-semibold tracking-tight text-foreground">
          {format(viewMonth, 'MMMM yyyy')}
        </span>
        <button
          type="button"
          onClick={() => onViewMonthChange((m) => addMonths(m, 1))}
          className={cn(
            'flex items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            navClass,
          )}
          aria-label="Next month"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center">
        {WEEKDAYS.map((wd) => (
          <div
            key={wd}
            className="py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70"
          >
            {wd.charAt(0)}
          </div>
        ))}
        {days.map((d) => {
          const inMonth = isSameMonth(d, viewMonth);
          const selected = selectedDate ? isSameDay(d, selectedDate) : false;
          const today = isToday(d);
          const disabled = isDayDisabled?.(d) ?? false;
          return (
            <button
              key={d.toISOString()}
              type="button"
              disabled={disabled}
              onClick={() => !disabled && onDaySelect(d)}
              className={cn(
                'mx-auto flex items-center justify-center rounded-lg font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                dayClass,
                !inMonth && 'text-muted-foreground/40',
                inMonth && !selected && !today && 'text-foreground hover:bg-accent hover:text-accent-foreground',
                today && !selected && 'text-primary ring-1 ring-inset ring-primary/40 hover:bg-accent',
                selected && 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90',
                disabled && 'cursor-not-allowed opacity-40 hover:bg-transparent',
              )}
            >
              {format(d, 'd')}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarGrid;
